import DOMPurify from 'dompurify'
import { marked } from 'marked'
import { Link } from 'react-router-dom'
import { newsletterAPI } from '../services/api'
import type { Content } from '../types'

/** Where one idea can go at once. The newsletter gets a long-form draft. */
export const PACK_NETWORKS: { id: string; label: string }[] = [
  { id: 'twitter', label: 'X' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'newsletter', label: 'Newsletter' },
]

export interface PackResult {
  /** Library drafts written. */
  drafts: number[]
  /** A newsletter issue drafted from the long-form post. */
  newsletter: boolean
  /** Networks that failed, with why. */
  failed: { network: string; reason: string }[]
}

const label = (id: string) => PACK_NETWORKS.find((n) => n.id === id)?.label ?? id

/**
 * Write one idea for each picked network, one after another (each is its
 * own draft, checked like any other). For the newsletter, the long-form
 * (blog) draft becomes a newsletter draft too, ready to edit and send.
 */
export const writePack = async (
  networks: string[],
  writeOne: (contentType: string) => Promise<Content>,
  reasonOf: (e: unknown) => string
): Promise<PackResult> => {
  const result: PackResult = { drafts: [], newsletter: false, failed: [] }
  for (const network of networks) {
    try {
      if (network === 'newsletter') {
        const longForm = await writeOne('blog')
        result.drafts.push(longForm.id)
        const text = longForm.text || ''
        await newsletterAPI.create({
          subject: (longForm.title || text.split('\n')[0] || 'This week').replace(/^#+\s*/, '').slice(0, 255),
          html_body: DOMPurify.sanitize(marked.parse(text, { async: false })),
          text_body: text,
        })
        result.newsletter = true
      } else {
        result.drafts.push((await writeOne(network)).id)
      }
    } catch (e) {
      result.failed.push({ network: label(network), reason: reasonOf(e) })
    }
  }
  return result
}

interface NetworkPicksProps {
  picked: string[]
  onChange: (picked: string[]) => void
  /** Leave out a network (the newsletter for a recap, which has its own day). */
  without?: string[]
}

/** Checkboxes for where to write it: one or several at once. */
export const NetworkPicks = ({ picked, onChange, without = [] }: NetworkPicksProps) => (
  <div role="group" aria-label="Write it for" className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-700">
    {PACK_NETWORKS.filter((n) => !without.includes(n.id)).map((n) => (
      <label key={n.id} className="flex items-center gap-1">
        <input
          type="checkbox"
          checked={picked.includes(n.id)}
          onChange={() => onChange(picked.includes(n.id) ? picked.filter((p) => p !== n.id) : [...picked, n.id])}
        />
        {n.label}
      </label>
    ))}
  </div>
)

/** What a pack produced: links to the drafts, and what didn't work. */
export const PackDone = ({ result }: { result: PackResult }) => (
  <div className="text-sm">
    {result.drafts.length > 0 && (
      <Link to="/content" state={{ openIds: result.drafts }} className="text-primary-600 hover:underline">
        {result.drafts.length === 1 ? 'Drafted: open in Library' : `${result.drafts.length} drafts: open in Library`}
      </Link>
    )}
    {result.newsletter && (
      <>
        {' · '}
        <Link to="/content/newsletter" className="text-primary-600 hover:underline">
          Newsletter draft
        </Link>
      </>
    )}
    {result.failed.map((f) => (
      <p key={f.network} role="alert" className="text-danger-700">
        {f.network}: {f.reason}
      </p>
    ))}
  </div>
)
